import {
  Component,
  effect,
  output,
  signal,
  input,
  contentChildren,
  untracked,
} from '@angular/core';
import { TabBarItemComponent } from './tab-bar-item/tab-bar-item';
@Component({
  selector: 'app-tab-bar',
  // The template and styles are assumed to be in these files.
  // No changes are needed in them for this refactoring.
  templateUrl: './tab-bar.html',
  styleUrls: ['./tab-bar.scss'],
})
export class TabBarComponent {
  // --- Inputs, Outputs, and Content Children as Signals ---

  // Renamed for clarity: this is the initial index coming from the parent.
  activeTabIndex = input(0);
  opaque = input(false);
  icon = input(false);
  disabled = input(false);
  fullWidth = input(false);

  // 'output' replaces the need for EventEmitter.
  tabChanged = output<number>();
  items = contentChildren(TabBarItemComponent);
  // Renamed for clarity: this is the internal, writable state of the active tab.
  _activeTabIndex = signal(0);
  init = signal(true);

  constructor() {
    // Only react to activeTabIndex() changes; ignore anything read inside onTabSelect
    effect(() => {
      const idx = this.activeTabIndex();
      untracked(() => this.onTabSelect(idx, true));
    });

    effect(() => {
      const isDisabled = this.disabled();
      const isOpaque = this.opaque();
      const currentIndex = this._activeTabIndex();

      this.items().forEach((component, index) => {
        component.index.set(index);
        component.tabBarReference = this;
        component.opaque.set(isOpaque);
        component.disabled.set(isDisabled);

        const isActive = index === currentIndex;
        component.preIsActive.set(isActive);
        component.isActive.set(isActive);
      });
    });
  }

  /**
   * Selects a tab and triggers the animation sequence.
   * This method can be called by child tabs or by the 'activeTabIndex' input effect.
   * @param index The index of the tab to select.
   * @param noemit Suppresses the tabChanged output event. Used for internal state sync.
   */
  onTabSelect(index: number, noemit = false): void {
    if (this.disabled()) {
      return; // Do nothing if the tab bar is disabled.
    }

    const previousIndex = this._activeTabIndex();
    // Calculate the direction of the marker animation.
    const move = this.init() && noemit ? '0' : `${index - previousIndex}`;

    // Update internal state signals. This is the correct way to assign a new value.
    this.init.set(false);
    this._activeTabIndex.set(index);

    // The animation logic remains similar but no longer requires manual change detection.
    setTimeout(() => {
      const currentIndex = this._activeTabIndex();
      this.items().forEach((component, _index) => {
        if (component.isActive()) {
          component.moveMarker.set(move);
        }
        component.preIsActive.set(_index === currentIndex);
        component.inMovement.set(true);

        setTimeout(() => {
          component.isActive.set(_index === currentIndex);
          component.moveMarker.set('0');
          component.inMovement.set(false);
        }, 250); // Animation duration
      });
    }, 0);

    if (!noemit) {
      setTimeout(() => {
        this.tabChanged.emit(index);
      }, 250); // Emit event after animation completes.
    }
  }
}
